import { useAuthStore } from '../stores/auth.store'
import type { ApiError } from '../types/api.types'
import { MODULE_IDS } from '../config/oauth.constants'
import { env } from '../config/env'

const API_BASE_URL = env.apiUrl

export type AuthMode = 'bearer' | 'legacy' | 'none'

export interface RequestConfig {
  auth?: AuthMode
  responseType?: 'json' | 'blob' | 'text'
}

type QueryParams = Record<string, string | number | boolean | null | undefined>

function buildUrl(endpoint: string, params?: QueryParams, authMode: AuthMode = 'bearer'): string {
  const authStore = useAuthStore()

  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
  const path = endpoint.startsWith('/') ? endpoint : '/' + endpoint
  const fullPath = base + path

  const absoluteUrl = fullPath.startsWith('/') ? window.location.origin + fullPath : fullPath

  const url = new URL(absoluteUrl)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.append(key, String(value))
      }
    })
  }

  if (authMode === 'legacy' && authStore.token && authStore.user) {
    url.searchParams.append('xid', String(authStore.user.id))
    url.searchParams.append('xtot1', authStore.token)

    if (!params || !('xmod' in params)) {
      url.searchParams.append('xmod', String(MODULE_IDS.BACKOFFICE))
    }
  }

  return url.toString()
}

function getHeaders(authMode: AuthMode = 'bearer'): HeadersInit {
  const authStore = useAuthStore()
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (authMode === 'bearer' && authStore.token) {
    headers['Authorization'] = `Bearer ${authStore.token}`
  }

  return headers
}

export type UnauthorizedReason = 'http_401' | 'network_error' | 'token_expired'

let unauthorizedCallback: ((reason: UnauthorizedReason) => void) | null = null

export function onUnauthorized(callback: (reason: UnauthorizedReason) => void): void {
  unauthorizedCallback = callback
}

function triggerSessionExpired(reason: UnauthorizedReason): void {
  if (unauthorizedCallback) {
    unauthorizedCallback(reason)
  } else if (typeof window !== 'undefined') {
    window.location.href = '/login'
  }
}

async function executeFetch(
  url: string,
  options: RequestInit,
  wasAuthenticated: boolean = false
): Promise<Response> {
  try {
    return await fetch(url, options)
  } catch (networkError) {
    if (wasAuthenticated && navigator.onLine) {
      triggerSessionExpired('network_error')
    }

    const error: ApiError = {
      status: 0,
      message:
        networkError instanceof Error
          ? networkError.message
          : 'Error de conexión. Verifique su conexión a internet.',
      error_code: 'NETWORK_ERROR',
      isSessionExpired: false,
    }
    throw error
  }
}

async function handleResponse<T>(
  response: Response,
  config: RequestConfig = { auth: 'bearer' },
  wasAuthenticated: boolean = false
): Promise<T> {
  if (!response.ok) {
    let errorMessage = response.statusText
    let errorCode: string | undefined
    let errors: Record<string | number, string> | undefined
    let responseData: unknown
    try {
      const errorData = await response.json()
      responseData = errorData
      errorMessage = errorData.message || errorData.error || errorMessage
      errorCode = errorData.error_code
      if (errorData.errors && typeof errorData.errors === 'object') {
        errors = errorData.errors
      }
    } catch {}

    const isTokenExpiredCode = errorCode === 'TOKEN_EXPIRED'
    const isSessionExpired = wasAuthenticated && (response.status === 401 || isTokenExpiredCode)

    const error: ApiError = {
      status: response.status,
      message: errorMessage,
      error_code: errorCode,
      errors,
      data: responseData,
      isSessionExpired,
    }

    if (isSessionExpired) {
      triggerSessionExpired(isTokenExpiredCode ? 'token_expired' : 'http_401')
    }

    throw error
  }

  if (response.status === 204) {
    if (config.responseType === 'blob') return new Blob() as unknown as T
    if (config.responseType === 'text') return '' as unknown as T
    return {} as T
  }

  if (config.responseType === 'blob') {
    return response.blob() as unknown as T
  }

  if (config.responseType === 'text') {
    return response.text() as unknown as T
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  return {} as T
}

export async function apiGet<T>(
  endpoint: string,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const headers = getHeaders(config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const response = await executeFetch(url, { method: 'GET', headers }, wasAuthenticated)

  return handleResponse<T>(response, config, wasAuthenticated)
}

export async function apiPost<T, D = object>(
  endpoint: string,
  data?: D,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const headers = getHeaders(config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const response = await executeFetch(
    url,
    { method: 'POST', headers, body: data ? JSON.stringify(data) : undefined },
    wasAuthenticated
  )

  return handleResponse<T>(response, config, wasAuthenticated)
}

export async function apiPut<T, D = object>(
  endpoint: string,
  data?: D,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const response = await executeFetch(
    url,
    {
      method: 'PUT',
      headers: getHeaders(config.auth),
      body: data ? JSON.stringify(data) : undefined,
    },
    wasAuthenticated
  )

  return handleResponse<T>(response, config, wasAuthenticated)
}

export async function apiPatch<T, D = object>(
  endpoint: string,
  data?: D,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const headers = getHeaders(config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const response = await executeFetch(
    url,
    { method: 'PATCH', headers, body: data ? JSON.stringify(data) : undefined },
    wasAuthenticated
  )

  return handleResponse<T>(response, config, wasAuthenticated)
}

export async function apiDelete<T>(
  endpoint: string,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const response = await executeFetch(
    url,
    { method: 'DELETE', headers: getHeaders(config.auth) },
    wasAuthenticated
  )

  return handleResponse<T>(response, config, wasAuthenticated)
}

export async function apiPostFormData<T>(
  endpoint: string,
  formData: FormData,
  params?: QueryParams,
  config: RequestConfig = { auth: 'bearer' }
): Promise<T> {
  const authStore = useAuthStore()
  const url = buildUrl(endpoint, params, config.auth)
  const wasAuthenticated = config.auth !== 'none' && !!authStore.token

  const headers: HeadersInit = {}
  if (config.auth === 'bearer' && authStore.token) {
    headers['Authorization'] = `Bearer ${authStore.token}`
  }

  const response = await executeFetch(
    url,
    { method: 'POST', headers, body: formData },
    wasAuthenticated
  )

  return handleResponse<T>(response, config, wasAuthenticated)
}
